import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  clearApiCache,
  fetchUserProfile,
  normalizeUserProfile,
  refreshAuthToken,
  UserProfile,
} from '../services/api';
import { getApiLanguage } from '../config/apiLanguage';
import { Sentry } from '../sentry';

export interface User {
  id: number;
  token: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  birthday?: string;
  profilePicture?: string;
  verifiedEmail?: boolean;
}

export type EstablishSessionInput = {
  id: number;
  token: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  verifiedEmail?: boolean;
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  establishSession: (input: EstablishSessionInput) => void;
  logout: () => void;
  isLoading: boolean;
  refreshUserProfile: () => Promise<void>;
  /** iOS parity: refresh JWT before protected API calls; logs out if refresh fails. */
  refreshSession: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseVerified(value: unknown): boolean {
  return value === 1 || value === true || String(value) === '1';
}

function readStoredUserShell(id: number, token: string): User {
  return {
    id,
    token,
    email: localStorage.getItem('auth_email') || undefined,
    firstName: localStorage.getItem('auth_first_name') || undefined,
    lastName: localStorage.getItem('auth_last_name') || undefined,
    birthday: localStorage.getItem('auth_birthday') || undefined,
    verifiedEmail: localStorage.getItem('auth_verified_email') === '1',
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: String(user.id),
      email: user.email,
      username: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
    });
  }, [user]);

  const logout = useCallback(() => {
    Sentry.setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user_id');
    localStorage.removeItem('auth_email');
    localStorage.removeItem('auth_first_name');
    localStorage.removeItem('auth_last_name');
    localStorage.removeItem('auth_birthday');
    localStorage.removeItem('auth_verified_email');
    clearApiCache();
    setUser(null);
  }, []);

  const persistToken = useCallback((token: string) => {
    localStorage.setItem('auth_token', token);
    setUser((prev) => {
      if (!prev) return prev;
      if (prev.token === token) return prev;
      return { ...prev, token };
    });
  }, []);

  const refreshSession = useCallback(async (): Promise<string | null> => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      return null;
    }
    const result = await refreshAuthToken(storedToken, getApiLanguage());
    if (!result.ok) {
      logout();
      return null;
    }
    persistToken(result.token);
    return result.token;
  }, [logout, persistToken]);

  const applyProfile = useCallback((profile: UserProfile | Record<string, unknown>, token: string) => {
    const normalized = normalizeUserProfile({ ...profile } as Record<string, unknown>);
    const verified = parseVerified(normalized.verified_email);
    const next: User = {
      id: normalized.id,
      token,
      email: normalized.email,
      firstName: normalized.firstName,
      lastName: normalized.lastName,
      birthday: normalized.birthday,
      profilePicture: normalized.profilePicture,
      verifiedEmail: verified,
    };
    setUser(next);
    localStorage.setItem('auth_verified_email', verified ? '1' : '0');
    if (normalized.email) localStorage.setItem('auth_email', normalized.email);
    if (normalized.firstName) localStorage.setItem('auth_first_name', normalized.firstName);
    else localStorage.removeItem('auth_first_name');
    if (normalized.lastName) localStorage.setItem('auth_last_name', normalized.lastName);
    else localStorage.removeItem('auth_last_name');
    if (normalized.birthday) localStorage.setItem('auth_birthday', normalized.birthday);
    else localStorage.removeItem('auth_birthday');
    return next;
  }, []);

  const refreshUserProfile = useCallback(async () => {
    const storedUserId = localStorage.getItem('auth_user_id');
    if (!storedUserId) {
      return;
    }
    const token = await refreshSession();
    if (!token) {
      return;
    }
    const id = parseInt(storedUserId, 10);
    try {
      const { user: profile } = await fetchUserProfile(id, token, getApiLanguage());
      applyProfile(profile, token);
    } catch {
      setUser(readStoredUserShell(id, token));
    }
  }, [applyProfile, refreshSession]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUserId = localStorage.getItem('auth_user_id');

      if (!storedToken || !storedUserId) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      const id = parseInt(storedUserId, 10);
      const result = await refreshAuthToken(storedToken, getApiLanguage());
      if (cancelled) return;

      if (!result.ok) {
        logout();
        setIsLoading(false);
        return;
      }

      const token = result.token;
      localStorage.setItem('auth_token', token);
      setUser(readStoredUserShell(id, token));

      try {
        const { user: profile } = await fetchUserProfile(id, token, getApiLanguage());
        if (!cancelled) applyProfile(profile, token);
      } catch {
        if (!cancelled) setUser(readStoredUserShell(id, token));
      }

      if (!cancelled) setIsLoading(false);
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyProfile, logout]);

  const establishSession = useCallback((input: EstablishSessionInput) => {
    const verified = parseVerified(input.verifiedEmail ?? true);
    const userData: User = {
      id: input.id,
      token: input.token,
      email: input.email,
      firstName: input.firstName,
      lastName: input.lastName,
      verifiedEmail: verified,
    };

    localStorage.setItem('auth_token', userData.token);
    localStorage.setItem('auth_user_id', userData.id.toString());
    localStorage.setItem('auth_verified_email', verified ? '1' : '0');
    if (userData.email) localStorage.setItem('auth_email', userData.email);
    else localStorage.removeItem('auth_email');
    if (userData.firstName) localStorage.setItem('auth_first_name', userData.firstName);
    else localStorage.removeItem('auth_first_name');
    if (userData.lastName) localStorage.setItem('auth_last_name', userData.lastName);
    else localStorage.removeItem('auth_last_name');

    setUser(userData);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        process.env.VITE_API_URL ||
        (typeof window !== 'undefined' ? window.location.origin : 'https://api.visime.com');
      const response = await fetch(`${apiBase}/api/v1.1/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: new URLSearchParams({
          email,
          password,
          language: getApiLanguage(),
        }),
      });

      const data = await response.json();

      if (data.error || (data.message && !data.token)) {
        throw new Error(data.message || data.error || 'Login failed');
      }

      const userId = data.user?.id || data.id;
      const token = data.token;

      if (token && userId) {
        const verified = parseVerified(data.user?.verified_email ?? data.verified_email);
        establishSession({
          id: userId,
          token,
          email: data.user?.email || data.email,
          firstName:
            data.user?.firstname || data.user?.firstName || data.first_name || data.firstName,
          lastName: data.user?.lastname || data.user?.lastName || data.last_name || data.lastName,
          verifiedEmail: verified,
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Login failed. Please check your connection.');
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    establishSession,
    logout,
    isLoading,
    refreshUserProfile,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
